import { useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'

import type { InvoiceDispute } from '@/shared/domain'
import { formatCurrency, formatDate } from '@/shared/infra/format'
import { reportError } from '@/shared/infra/observability'
import { Button } from '@/shared/ui/primitives'

export type InvoiceDisputePanelProps = {
  dispute: InvoiceDispute
  onRespond: () => Promise<void>
}

function humanizeReason(reason?: string): string {
  if (!reason) return 'Unknown reason'
  return reason
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function isOverdue(dispute: InvoiceDispute): boolean {
  if (!dispute.evidenceDueBy) return false
  return new Date(dispute.evidenceDueBy).getTime() < Date.now()
}

export function InvoiceDisputePanel({ dispute, onRespond }: InvoiceDisputePanelProps) {
  const [isResolving, setIsResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const overdue = isOverdue(dispute)
  const formattedDeadline = dispute.evidenceDueBy
    ? formatDate(dispute.evidenceDueBy)
    : null

  const handleRespond = async () => {
    setError(null)
    setIsResolving(true)
    try {
      await onRespond()
    } catch (err) {
      const message = reportError(err, {
        fallbackMessage: 'Failed to open the Stripe dashboard. Please try again.',
      })
      setError(message)
    } finally {
      setIsResolving(false)
    }
  }

  return (
    <div className="rounded-md border border-amber/30 bg-amber-soft p-4">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber" />
        <h4 className="text-sm font-semibold text-amber">Payment disputed</h4>
      </div>
      <p className="mb-1 text-xs text-muted-foreground">
        Reason:{' '}
        <span className="font-medium text-foreground">
          {humanizeReason(dispute.reason)}
        </span>
      </p>
      <p className="mb-1 text-xs text-muted-foreground">
        Amount:{' '}
        <span className="font-medium text-foreground">
          {formatCurrency(dispute.amountCents / 100)}
        </span>
      </p>
      {formattedDeadline ? (
        <p className="mb-3 text-xs text-muted-foreground">
          Evidence deadline:{' '}
          {overdue ? (
            <span className="font-medium text-destructive">Overdue</span>
          ) : (
            <span className="font-medium text-foreground">{formattedDeadline}</span>
          )}
        </p>
      ) : (
        <div className="mb-3" />
      )}
      <p className="mb-3 text-xs text-muted-foreground">
        Responding to a dispute happens in the Stripe dashboard under Payments &rarr;
        Disputes.
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleRespond}
        disabled={isResolving}
      >
        {isResolving ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <ExternalLink className="size-4" />
        )}
        Respond on Stripe
      </Button>
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
